import { useCallback } from "react";

import { useProductDependencies } from "../../../hooks/query";
import { applyProductDependencies } from "../utils/applyProductDependencies";
import {
    findOptionByKey,
    findOptionForProductId,
    resolveVariantProductId,
} from "../utils/materialColorSelection";
import type { MaterialLineItemOption } from "../types";
import type { MaterialRowItem } from "../utils/materialLineItems";
import useEstimateContractContextManager from "./useEstimateContractContextManager";

export function useMaterialProductSelection(
    materialsByGroup: Record<string, MaterialRowItem[]>,
) {
    const { useList } = useProductDependencies();
    const { data: dependencies } = useList();
    const { values, patchMaterialEdits } = useEstimateContractContextManager();

    const onProductChange = useCallback(
        (
            editKey: string,
            category: string,
            _rowId: string,
            optionKey: string | null,
            options: MaterialLineItemOption[],
        ) => {
            const option = findOptionByKey(options, optionKey);
            const selectedColor =
                values.materials[editKey]?.selectedColor ?? null;
            const productId = resolveVariantProductId(option, selectedColor);

            const patches = applyProductDependencies({
                editKey,
                category,
                selectedProductId: productId,
                dependencies,
                materialsByGroup,
            });

            if (patches[editKey]) {
                patches[editKey] = {
                    ...patches[editKey],
                    selectedColor: selectedColor || null,
                };
            }

            patchMaterialEdits(patches);
        },
        [dependencies, materialsByGroup, patchMaterialEdits, values.materials],
    );

    const onCatalogColorChange = useCallback(
        (editKey: string, color: string, options: MaterialLineItemOption[]) => {
            const currentId =
                values.materials[editKey]?.selectedProductId ?? null;
            const option =
                findOptionForProductId(options, currentId) || options[0] || null;
            const productId = resolveVariantProductId(option, color || null);

            patchMaterialEdits({
                [editKey]: {
                    selectedProductId: productId,
                    selectedColor: color || null,
                },
            });
        },
        [patchMaterialEdits, values.materials],
    );

    return { onProductChange, onCatalogColorChange };
}
